/**
 * Conversation Sidebar Component
 * Shows list of conversations with drag-and-drop reordering.
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArchiveBoxIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useConversation } from '@/app/providers/ConversationContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { useSession } from '@/features/auth/hooks/useSession';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { isFreeTier } from '@/features/billing/lib/tier-predicates';
import { useQueryClient } from '@tanstack/react-query';
import { useReorderConversationsMutation } from '@/features/conversations/hooks/useReorderConversationsMutation';
import { useIsModuleTypeHidden } from '@/features/entitlements/hooks/useEntitlements';
import type { Conversation } from '@/types/conversations';
import { EditConversationModal } from './EditConversationModal';
import { DataBackupsModal } from './DataBackupsModal';
import { ArchivedModal } from './ArchivedModal';
import { ProfileModal } from './ProfileModal';
import { MobileSettingsModal } from './MobileSettingsModal';
import { PlanChoiceModal } from '@/features/billing/components/PlanChoiceModal';
import './ConversationSidebar.css';
import { useFeedback } from '@/app/providers/FeedbackContext';
import { useToast } from '@/app/providers/ToastContext';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { useIsCompactLayout } from '@/common/hooks';
import { SidebarRail } from './SidebarRail';
import { SkeletonConversationList } from '@/common/components/Skeleton';
import { TrialBanner } from '@/features/billing/components';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import {
  orderConversationsWithPinnedFirst,
} from './sidebar/utils';
import type {
  ConversationSidebarProps,
  SidebarModuleItem,
} from './sidebar/types';
export type { ConversationSidebarProps } from './sidebar/types';
import { useCollapseState } from './sidebar/hooks/useCollapseState';
import { useConversationActions } from './sidebar/hooks/useConversationActions';
import { useDragModeEffects } from './sidebar/hooks/useDragModeEffects';
import { useModuleCreation } from './sidebar/hooks/useModuleCreation';
import { useDeleteModals } from './sidebar/hooks/useDeleteModals';
import { useDragReorder } from './sidebar/hooks/useDragReorder';
import { useModuleDragReorder } from './sidebar/hooks/useModuleDragReorder';
import { useSidebarContextMenus } from './sidebar/hooks/useSidebarContextMenus';
import { useSidebarModulesData } from './sidebar/hooks/useSidebarModulesData';
import { useSidebarOverflow } from './sidebar/hooks/useSidebarOverflow';
import { useModuleLaunch } from './sidebar/hooks/useModuleLaunch';
import { SidebarModuleItemRow } from './sidebar/SidebarModuleItemRow';
import { ConversationItemWithLongPress } from './sidebar/ConversationItemWithLongPress';
export { SidebarModuleItemRow } from './sidebar/SidebarModuleItemRow';
export { ConversationItemWithLongPress } from './sidebar/ConversationItemWithLongPress';
import { useClickOutside } from './sidebar/hooks/useClickOutside';
import { computeDisplayedConversations } from './sidebar/hooks/useDisplayedConversations';
import { computeFilteredModules, computeMobileRailGrouped } from './sidebar/hooks/useFilteredModules';
import { useSidebarKeyboardShortcuts } from './sidebar/hooks/useSidebarKeyboardShortcuts';
import { useSidebarWindowEvents } from './sidebar/hooks/useSidebarWindowEvents';
import { DeleteConfirmDialog } from './sidebar/components/DeleteConfirmDialog';
import { NewModuleModal } from './sidebar/components/NewModuleModal';
import { WorkspaceSwitcher } from './sidebar/components/WorkspaceSwitcher';
import { ConversationContextMenu } from './sidebar/components/ConversationContextMenu';
import { ModuleContextMenu } from './sidebar/components/ModuleContextMenu';
import { CollapsedSidebarContent } from './sidebar/components/CollapsedSidebarContent';

// Conversation ordering is now managed by the database via position column.
// The API returns conversations in the correct order per workspace.

export default function ConversationSidebar({
  isOpen = true,
  dragX,
  onConversationClick,
  onCloseSidebar,
  onCreateConversation: onCreateConversationProp,
}: ConversationSidebarProps) {
  const { allConversations, activeConversation, setActiveConversation, createConversation, deleteConversation, addModule, renamingConversationId, setRenamingConversationId, isLoading } = useConversation();
  const { clearSession, token, profile, containers, selectedContainer, selectContainer, fetchContainers, pendingInvites, isReadOnly } = useSession();
  const workspacePlanTier = useWorkspacePlanTier();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const navigate = useNavigate();
  const location = useLocation();
  const routeConversationId = useMemo(() => {
    const match = location.pathname.match(/^\/w\/[^/]+\/c\/([^/]+)/);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [location.pathname]);
  const routeModuleId = useMemo(() => {
    const match = location.pathname.match(/\/mod\/([^/]+)/);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [location.pathname]);
  // The URL's /mod/ segment can be STALE: closing a module to chat-only only
  // flips state, it never clears the route. So trust the live pane state
  // (isModulePaneOpen, published by ChatBox) for whether a module is actually
  // open — otherwise a switch from chat-only would reopen the last module via the
  // stale URL. When the pane IS open the route is accurate (opening navigates).
  const { isModulePaneOpen } = useChatDock();
  const currentOpenModuleId = isModulePaneOpen ? routeModuleId : null;
  const { openModal } = useFeedback();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsCompactLayout();
  const trialStatus = profile?.trialStatus ?? null;
  const handleUpgradeClick = useCallback(() => {
    if (!token) { navigate('/login', { replace: true }); return; }
    window.dispatchEvent(new CustomEvent('xavior:open-upgrade-modal'));
  }, [navigate, token]);
  const reorderMutation = useReorderConversationsMutation();
  const [isDragMode, setIsDragMode] = useState(false);
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false);

  const orderedUserConversations = useMemo(
    () => orderConversationsWithPinnedFirst(allConversations),
    [allConversations],
  );

  const {
    sidebarModuleItems,
    allModulesSorted,
  } = useSidebarModulesData(token, workspaceId);

  const [isDataBackupsModalOpen, setIsDataBackupsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isArchivedConversationsModalOpen, setIsArchivedConversationsModalOpen] = useState(false);
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = useState(false);
  const [isEditConversationModalOpen, setIsEditConversationModalOpen] = useState(false);
  const [editingConversation, setEditingConversation] = useState<Conversation | null>(null);
  const [isChatsSectionOpen, setIsChatsSectionOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; conversation: Conversation } | null>(null);
  const [moduleContextMenu, setModuleContextMenu] = useState<{ x: number; y: number; item: SidebarModuleItem } | null>(null);
  const [renamingModuleId, setRenamingModuleId] = useState<string | null>(null);
  const [tempModuleOrder, setTempModuleOrder] = useState<SidebarModuleItem[] | null>(null);
  const [isAddWorkspaceModalOpen, setIsAddWorkspaceModalOpen] = useState(false);

  const [mobileShowAllChats, setMobileShowAllChats] = useState(false);
  const [expandedMobileRailGroups, setExpandedMobileRailGroups] = useState<Set<string>>(new Set());
  const [isConversationListOverflowing, setIsConversationListOverflowing] = useState(false);
  const [isChatsGearMenuOpen, setIsChatsGearMenuOpen] = useState(false);

  const chatsGearMenuRef = useRef<HTMLDivElement>(null);
  const chatsGearBtnRef = useRef<HTMLButtonElement>(null);
  const editingConversationNameRefs = useRef<{ [conversationId: string]: HTMLSpanElement | null }>({});
  const editingModuleNameRefs = useRef<{ [moduleId: string]: HTMLSpanElement | null }>({});
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const moduleContextMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const conversationListRef = useRef<HTMLElement>(null);
  const lastModuleRecencyPersistRef = useRef<{ moduleId: string; timestamp: number } | null>(null);

  const {
    isCollapsed,
    setIsCollapsed,
    isCollapseLogoSwapLocked,
    isCollapsedHoverSwapReady,
    isCollapsedHoverResetPending,
    setIsCollapsedHoverSwapReady,
    setIsCollapsedHoverResetPending,
    clearCollapseLogoSwapLock,
    lockLogoSwapDuringCollapse,
  } = useCollapseState({ sidebarRef });

  const {
    handleCreateConversation,
    handleConversationClick,
    handleConversationLongPress,
    handleSaveConversationName,
    handleConversationNameKeyDown,
    handleArchiveConversation,
    handleArchiveAllConversations,
  } = useConversationActions({
    onCreateConversationProp,
    onCloseSidebar,
    onConversationClick,
    allConversations,
    activeConversation,
    createConversation,
    deleteConversation,
    showToast,
    isDragMode,
    setIsDragMode,
    renamingConversationId,
    setRenamingConversationId,
    isReadOnly,
    navigate,
    workspaceId,
    currentModuleId: currentOpenModuleId,
    editingConversationNameRefs,
    token,
    queryClient,
    setActiveConversation,
    userConversations: orderedUserConversations,
    setIsChatsGearMenuOpen,
  });

  // Pane authority (ADR 028) — module opens record intent before navigating.
  const { openPane } = useModulePane();
  const { handleOpenSidebarModule } = useModuleLaunch({
    token,
    workspaceId,
    queryClient,
    activeConversation,
    orderedUserConversations,
    routeConversationId,
    setActiveConversation,
    navigate,
    onCloseSidebar,
    lastModuleRecencyPersistRef,
    openPane,
  });

  const {
    isNewModuleMenuOpen,
    setIsNewModuleMenuOpen,
    newModuleStep,
    setNewModuleStep,
    newModuleName,
    setNewModuleName,
    newModuleNameError,
    setNewModuleNameError,
    newModuleSearch,
    setNewModuleSearch,
    isCreatingModule,
    newModuleNameInputRef,
    handleCreateModule,
  } = useModuleCreation({
    activeConversation,
    addModule,
    navigate,
    workspaceId,
    queryClient,
  });

  const {
    deleteConversationTarget,
    setDeleteConversationTarget,
    isDeleteConversationPending,
    deleteModuleTarget,
    setDeleteModuleTarget,
    isDeleteModulePending,
    handleCloseDeleteConversationModal,
    handleConfirmDeleteConversation,
    handleCloseDeleteModuleModal,
    handleConfirmDeleteModule,
  } = useDeleteModals({
    deleteConversation,
    token,
    workspaceId,
    queryClient,
  });

  // Close context menu when clicking outside
  useClickOutside([contextMenuRef], () => setContextMenu(null), Boolean(contextMenu));

  // Close module context menu when clicking outside
  useClickOutside([moduleContextMenuRef], () => setModuleContextMenu(null), Boolean(moduleContextMenu));

  // Close chats gear menu when clicking outside
  useClickOutside(
    [chatsGearMenuRef, chatsGearBtnRef],
    () => setIsChatsGearMenuOpen(false),
    isChatsGearMenuOpen,
  );

  // #3447: a user with no selected/owned container isn't an owner of anything —
  // default the placeholder to isOwner:false so the zero-workspace state never
  // surfaces owner-only affordances.
  const displayContainer = selectedContainer || containers[0] || {
    isOwner: false,
    containerName: null,
    ownerId: '',
    ownerName: null,
    ownerEmail: null
  };
  const showWorkspaceSwitcher = true; // Always show workspace switcher

  useDragModeEffects({
    isDragMode,
    setIsDragMode,
    isCollapsed,
    searchQuery,
    setSearchQuery,
    sidebarRef,
  });

  useSidebarKeyboardShortcuts({ isMobile, isCollapsed, setIsCollapsed, searchInputRef });

  useSidebarWindowEvents({
    isMobile,
    handleCreateConversation,
    lockLogoSwapDuringCollapse,
    clearCollapseLogoSwapLock,
    setIsMobileSettingsOpen,
    setIsNewModuleMenuOpen,
    setNewModuleStep,
    setNewModuleName,
    setNewModuleNameError,
    setIsCollapsed,
    setIsDragMode,
    setContextMenu,
    setIsDataBackupsModalOpen,
  });

  const userConversations = orderedUserConversations;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const {
    draggedIndex,
    tempConversationOrder,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragReorder({
    userConversations,
    orderedUserConversations,
    reorderMutation,
  });

  const {
    handleConversationContextMenu,
    handleConversationActionsClick,
    handleRenameFromContextMenu,
    handleArchiveFromContextMenu,
    handlePinFromContextMenu,
    handleModuleLongPress,
    handleModuleContextMenu,
    handleModuleActionsClick,
    handleArchiveModuleFromContextMenu,
    handleRenameModuleFromContextMenu,
    handleSaveModuleName,
    handleModuleNameBlur,
    handleModuleNameKeyDown,
    handleDeleteFromContextMenu,
  } = useSidebarContextMenus({
    contextMenu,
    setContextMenu,
    moduleContextMenu,
    setModuleContextMenu,
    token,
    workspaceId,
    activeConversation,
    setActiveConversation,
    queryClient,
    tempConversationOrder,
    userConversations,
    isReadOnly,
    showToast,
    setRenamingConversationId,
    editingConversationNameRefs,
    setDeleteConversationTarget,
    setRenamingModuleId,
    editingModuleNameRefs,
  });

  const displayedConversations = useMemo(
    () => computeDisplayedConversations({ tempConversationOrder, userConversations, normalizedSearchQuery }),
    [tempConversationOrder, userConversations, normalizedSearchQuery],
  );
  // Entitlement gate (ADR 042, WEB-8): a module whose feature verdict is
  // `hidden` gets no sidebar launch affordance. Inert with an empty catalog
  // (no module type is hidden), so this is a pure no-op until a feature is
  // catalogued + set hidden.
  const isModuleTypeHidden = useIsModuleTypeHidden(workspaceId);
  const filteredModules = useMemo(
    () =>
      computeFilteredModules({ tempModuleOrder, allModulesSorted, normalizedSearchQuery }).filter(
        (item) => !isModuleTypeHidden(item.module.type),
      ),
    [allModulesSorted, tempModuleOrder, normalizedSearchQuery, isModuleTypeHidden],
  );

  // Group modules by rail type for mobile rail-style display
  const mobileRailGrouped = useMemo(
    () => computeMobileRailGrouped(filteredModules),
    [filteredModules],
  );

  const {
    handleModuleDragStart,
    handleModuleDragOver,
    handleModuleDragEnd,
  } = useModuleDragReorder({
    filteredModules,
    tempModuleOrder,
    setTempModuleOrder,
    workspaceId,
    token,
    queryClient,
  });

  const shouldShowConversationList = !isCollapsed && (isMobile || isChatsSectionOpen);
  const displayName = profile?.name?.trim() || profile?.email?.split('@')[0] || 'Account';
  const displayInitial = displayName.charAt(0).toUpperCase();

  useSidebarOverflow({
    isMobile,
    conversationListRef,
    sidebarRef,
    setIsConversationListOverflowing,
    displayedConversations,
    isLoading,
    isCollapsed,
    isChatsSectionOpen,
  });

  const sidebarDragStyle: React.CSSProperties | undefined = dragX !== null && dragX !== undefined
    ? { transform: `translateX(${dragX}px)`, transition: 'none' }
    : undefined;

  return (
    <>
    <aside
      ref={sidebarRef}
      className={`conversation-sidebar ${isOpen ? 'mobile-open' : ''} ${isCollapsed ? 'collapsed' : ''} ${isCollapseLogoSwapLocked ? 'collapse-logo-swap-lock' : ''} ${isCollapsedHoverSwapReady ? 'collapsed-hover-swap-ready' : ''}`}
      style={sidebarDragStyle}
      onClick={isCollapsed ? (e) => {
        if (!(e.target as HTMLElement).closest('button, a, .conversation-item, .collapsed-item')) {
          clearCollapseLogoSwapLock();
          setIsCollapsed(false);
        }
      } : undefined}
      onMouseLeave={() => {
        if (isCollapsed && !isCollapseLogoSwapLocked && isCollapsedHoverResetPending) {
          setIsCollapsedHoverSwapReady(true);
          setIsCollapsedHoverResetPending(false);
        }
      }}
    >
      <div className="workspace-switcher">
        <div className="sidebar-brand">
          {/* When collapsed: logo that reveals expand icon on hover */}
          {isCollapsed ? (
            <button
              className="sidebar-brand-toggle"
              onClick={() => {
                clearCollapseLogoSwapLock();
                setIsCollapsed(false);
              }}
              title="Expand sidebar"
            >
              <img src="/favicon.svg" alt="Xavior" className="sidebar-brand-logo sidebar-brand-logo-default" />
              <svg className="sidebar-brand-logo-expand" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M14 9L17 12L14 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          ) : (
            <img src="/favicon.svg" alt="Xavior" className="sidebar-brand-logo" />
          )}
        </div>

        {/* Collapse sidebar button - top right of header (only when expanded) */}
        {!isMobile && !isCollapsed && (
          <button
            className="sidebar-collapse-btn"
            onClick={() => {
              lockLogoSwapDuringCollapse();
              setIsCollapsed(true);
            }}
            title="Collapse sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M17 9L14 12L17 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Top actions: New Chat, Integrations */}
      {!isMobile && (
        <>
          <div className="sidebar-top-actions">
            {!isReadOnly && (
              <button
                className="sidebar-new-chat-btn"
                onClick={handleCreateConversation}
                title="New Chat"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M10 10H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>New Chat</span>
              </button>
            )}
            {/* Integrations — hidden until ready */}
          </div>
          <div className="sidebar-top-actions-divider" />
        </>
      )}

      {/* Expandable search bar -- shown when search has content or focused via Cmd+K */}
      {!isMobile && !isCollapsed && (
        <div className={`sidebar-search-wrap${searchQuery ? ' active' : ''}`}>
          <input
            ref={searchInputRef}
            type="text"
            className="sidebar-search-input"
            placeholder="Search..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onFocus={(event) => event.currentTarget.parentElement?.classList.add('active')}
            onBlur={(event) => { if (!searchQuery) event.currentTarget.parentElement?.classList.remove('active'); }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setSearchQuery('');
                event.currentTarget.blur();
              }
            }}
          />
        </div>
      )}

      {/* Unified content: Recent Chats + Modules */}
      {!isMobile && !isCollapsed ? (
        <div className="sidebar-unified-content">
          {/* Recent Chats */}
          <div className="sidebar-section">
            <div className="sidebar-section-heading">
              Recent Chats
              <button
                ref={chatsGearBtnRef}
                className="sidebar-section-gear-btn"
                onClick={() => setIsChatsGearMenuOpen((prev) => !prev)}
                title="Chat options"
              >
                <Cog6ToothIcon style={{ width: 14, height: 14 }} />
              </button>
              {isChatsGearMenuOpen && (
                <div ref={chatsGearMenuRef} className="sidebar-section-gear-menu">
                  <button
                    className="context-menu-item"
                    onClick={() => {
                      setIsChatsGearMenuOpen(false);
                      setTimeout(() => searchInputRef.current?.focus(), 50);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="context-menu-item-icon">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Search Chats
                  </button>
                  <button
                    className="context-menu-item"
                    onClick={handleArchiveAllConversations}
                  >
                    <ArchiveBoxIcon className="context-menu-item-icon" />
                    Archive All
                  </button>
                </div>
              )}
            </div>
            <nav ref={conversationListRef} className={`conversation-list ${isDragMode ? 'drag-mode' : ''}`}>
              {shouldShowConversationList && (
                <>
                  {displayedConversations.length > 0 ? (
                    displayedConversations.map((conversation, index) => (
                      <ConversationItemWithLongPress
                        key={conversation.id}
                        conversation={conversation}
                        index={index}
                        isActive={conversation.id === activeConversation?.id}
                        isDragging={draggedIndex === index}
                        isActionsMenuOpen={contextMenu?.conversation.id === conversation.id}
                        isDragMode={isDragMode && !normalizedSearchQuery}
                        isMobile={isMobile}
                        isReadOnly={isReadOnly}
                        renamingConversationId={renamingConversationId}
                        editingConversationNameRefs={editingConversationNameRefs}
                        onConversationClick={handleConversationClick}
                        onConversationLongPress={handleConversationLongPress}
                        onConversationContextMenu={handleConversationContextMenu}
                        onConversationActionsClick={handleConversationActionsClick}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                        onArchiveConversation={handleArchiveConversation}
                        onSaveConversationName={handleSaveConversationName}
                        onConversationNameKeyDown={handleConversationNameKeyDown}
                      />
                    ))
                  ) : isLoading ? (
                    <SkeletonConversationList count={4} />
                  ) : normalizedSearchQuery ? (
                    <div className="no-conversations-message">
                      <p className="welcome-title">No chats found</p>
                      <p className="welcome-subtitle">Try a different keyword</p>
                    </div>
                  ) : (
                    <div className="no-conversations-message">
                      {isReadOnly ? (
                        <>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                          </svg>
                          <p className="welcome-title">View-only workspace</p>
                          <p className="welcome-subtitle">Select a conversation above to explore shared data</p>
                        </>
                      ) : (
                        <>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="16"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                          <p className="welcome-title">No conversations yet</p>
                          <p className="welcome-subtitle">Create your first conversation to get started</p>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </nav>
          </div>

          {/* Modules */}
          {filteredModules.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-heading">
                Modules ({filteredModules.length})
                <button
                  className="sidebar-section-gear-btn"
                  onClick={() => {
                    setIsNewModuleMenuOpen(true);
                    setNewModuleStep(null);
                    setNewModuleName('');
                    setNewModuleNameError('');
                  }}
                  title="New Module"
                  style={{ opacity: 0.7 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <nav className="conversation-list sidebar-all-modules-list">
                {filteredModules.map((item) => (
                  <SidebarModuleItemRow
                    key={item.module.id}
                    item={item}
                    isActive={item.module.id === routeModuleId}
                    isActionsMenuOpen={moduleContextMenu?.item.module.id === item.module.id}
                    isReadOnly={isReadOnly}
                    isMobile={isMobile}
                    onClick={handleOpenSidebarModule}
                    onLongPress={handleModuleLongPress}
                    onContextMenu={handleModuleContextMenu}
                    onActionsClick={handleModuleActionsClick}
                    draggable={!normalizedSearchQuery}
                    onDragStart={handleModuleDragStart}
                    onDragOver={handleModuleDragOver}
                    onDragEnd={handleModuleDragEnd}
                    renamingModuleId={renamingModuleId}
                    editingModuleNameRefs={editingModuleNameRefs}
                    onSaveModuleName={handleSaveModuleName}
                    onModuleNameBlur={handleModuleNameBlur}
                    onModuleNameKeyDown={handleModuleNameKeyDown}
                  />
                ))}
              </nav>
            </div>
          )}
        </div>
      ) : !isMobile && isCollapsed ? (
        /* Collapsed sidebar: show first-letter avatars for chats, emoji icons for modules */
        <CollapsedSidebarContent
          displayedConversations={displayedConversations}
          activeConversationId={activeConversation?.id}
          filteredModules={filteredModules}
          onConversationClick={handleConversationClick}
          onOpenModule={handleOpenSidebarModule}
        />
      ) : (
        /* Mobile: chats + modules in scrollable unified block */
        <div className="sidebar-unified-content">
          {/* Workspace Switcher (only when multiple containers) */}
          {showWorkspaceSwitcher && (
            <WorkspaceSwitcher
              containers={containers}
              selectedContainer={selectedContainer}
              workspaceSwitcherOpen={workspaceSwitcherOpen}
              setWorkspaceSwitcherOpen={setWorkspaceSwitcherOpen}
              selectContainer={selectContainer}
              setIsAddWorkspaceModalOpen={setIsAddWorkspaceModalOpen}
            />
          )}

          {/* Rail-style nav items */}
          <div className="mobile-rail-nav">
            {/* New Chat */}
            {!isReadOnly && (
              <button className="mobile-rail-item" onClick={handleCreateConversation}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>New Chat</span>
              </button>
            )}

            {/* History — collapsible */}
            <button
              className={`mobile-rail-item${mobileShowAllChats ? ' active' : ''}`}
              onClick={() => setMobileShowAllChats((prev) => !prev)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>History</span>
              {displayedConversations.length > 0 && (
                <span className="mobile-rail-badge">{displayedConversations.length}</span>
              )}
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className="mobile-rail-chevron"
                style={{ transform: mobileShowAllChats ? 'rotate(180deg)' : undefined }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          {/* Conversation list — shown when History is expanded */}
          {mobileShowAllChats && shouldShowConversationList && (
            <nav ref={conversationListRef} className="mobile-rail-history-list">
              {displayedConversations.length > 0 ? (
                displayedConversations.map((conversation, index) => (
                  <ConversationItemWithLongPress
                    key={conversation.id}
                    conversation={conversation}
                    index={index}
                    isActive={conversation.id === activeConversation?.id}
                    isDragging={draggedIndex === index}
                    isActionsMenuOpen={contextMenu?.conversation.id === conversation.id}
                    isDragMode={isDragMode && !normalizedSearchQuery}
                    isMobile={isMobile}
                    isReadOnly={isReadOnly}
                    renamingConversationId={renamingConversationId}
                    editingConversationNameRefs={editingConversationNameRefs}
                    onConversationClick={handleConversationClick}
                    onConversationLongPress={handleConversationLongPress}
                    onConversationContextMenu={handleConversationContextMenu}
                    onConversationActionsClick={handleConversationActionsClick}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onArchiveConversation={handleArchiveConversation}
                    onDeleteConversation={setDeleteConversationTarget}
                    onSaveConversationName={handleSaveConversationName}
                    onConversationNameKeyDown={handleConversationNameKeyDown}
                  />
                ))
              ) : isLoading ? (
                <SkeletonConversationList count={3} />
              ) : (
                <div className="no-conversations-message" style={{ padding: '8px 16px' }}>
                  <p className="welcome-subtitle" style={{ fontSize: '13px', margin: 0 }}>No conversations yet</p>
                </div>
              )}
            </nav>
          )}

          {/* Divider */}
          <div className="mobile-rail-divider" />

          {/* Modules — same SidebarRail component as desktop */}
          {activeConversation && (
            <SidebarRail
              onNewChat={handleCreateConversation}
              onOpenSettings={() => {}}
              onOpenIntegrations={() => {}}
              onOpenWorkspaceOptions={() => {}}
            />
          )}
        </div>
      )}

      {/* Old mobile profile avatar removed — replaced by mobile-sidebar-profile-section */}

      {/* Mobile edit mode header - shows when editing */}
      {isMobile && isDragMode && (
        <div className="mobile-edit-mode-header">
          <span className="mobile-edit-mode-hint">Tap archive icon to remove conversations</span>
          <button
            className="mobile-edit-mode-done"
            onClick={() => setIsDragMode(false)}
          >
            Done
          </button>
        </div>
      )}

      {/* Mobile settings menu moved to hamburger bottom sheet below */}

      {/* Generic "Upgrade to Pro" pill — FREE workspaces only. A `trial`
          workspace is deliberately excluded here (isFreeTier is false for trial):
          trial owners get the dedicated <TrialBanner> below (countdown + Upgrade),
          so showing this pill too would double the upgrade CTA (PDR 011 §9a). */}
      {workspacePlanTier !== null && isFreeTier(workspacePlanTier) && profile?.isPaidAccount === false && selectedContainer?.isOwner !== false && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('xavior:open-upgrade-modal'))}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-[13px] font-medium text-sky-600 hover:bg-sky-500/15 hover:border-sky-500/40 transition-colors"
          >
            <span aria-hidden="true">↑</span>
            Upgrade plan
          </button>
        </div>
      )}

      <div className={`sidebar-footer ${isConversationListOverflowing ? 'with-divider' : ''}`}>
        <TrialBanner
          trialStatus={trialStatus}
          isPaidAccount={profile?.isPaidAccount}
          onUpgradeClick={handleUpgradeClick}
        />
      </div>

      {/* Conversation Context Menu */}
      {contextMenu && (
        <ConversationContextMenu
          contextMenu={contextMenu}
          contextMenuRef={contextMenuRef}
          onRename={handleRenameFromContextMenu}
          onPin={handlePinFromContextMenu}
          onArchive={handleArchiveFromContextMenu}
          onDelete={handleDeleteFromContextMenu}
        />
      )}

      {moduleContextMenu && (
        <ModuleContextMenu
          moduleContextMenu={moduleContextMenu}
          moduleContextMenuRef={moduleContextMenuRef}
          isMobile={isMobile}
          setModuleContextMenu={setModuleContextMenu}
          setDeleteModuleTarget={setDeleteModuleTarget}
          onRename={handleRenameModuleFromContextMenu}
          onArchive={handleArchiveModuleFromContextMenu}
        />
      )}


      {deleteConversationTarget && (
        <DeleteConfirmDialog
          title="Delete chat?"
          targetName={deleteConversationTarget.name}
          isPending={isDeleteConversationPending}
          onClose={handleCloseDeleteConversationModal}
          onConfirm={handleConfirmDeleteConversation}
        />
      )}

      {deleteModuleTarget && (
        <DeleteConfirmDialog
          title="Delete module?"
          targetName={deleteModuleTarget.name}
          isPending={isDeleteModulePending}
          onClose={handleCloseDeleteModuleModal}
          onConfirm={handleConfirmDeleteModule}
        />
      )}

      <DataBackupsModal
        isOpen={isDataBackupsModalOpen}
        onClose={() => setIsDataBackupsModalOpen(false)}
      />

      <ArchivedModal
        isOpen={isArchivedConversationsModalOpen}
        onClose={() => setIsArchivedConversationsModalOpen(false)}
        conversationId={activeConversation?.id}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      <MobileSettingsModal
        isOpen={isMobileSettingsOpen}
        onClose={() => setIsMobileSettingsOpen(false)}
      />


      <EditConversationModal
        isOpen={isEditConversationModalOpen}
        onClose={() => {
          setIsEditConversationModalOpen(false);
          setEditingConversation(null);
        }}
        conversation={editingConversation}
      />

      <PlanChoiceModal
        isOpen={isAddWorkspaceModalOpen}
        onClose={() => setIsAddWorkspaceModalOpen(false)}
        onCreated={async (spaceId) => {
          // Refresh containers and switch into the new (owned) workspace so the
          // owner lands inside it — no full page reload. Mirrors SidebarRail.
          await fetchContainers();
          await selectContainer(spaceId);
        }}
      />


      {/* New Module modal */}
      {isNewModuleMenuOpen && (
        <NewModuleModal
          newModuleStep={newModuleStep}
          setNewModuleStep={setNewModuleStep}
          newModuleName={newModuleName}
          setNewModuleName={setNewModuleName}
          newModuleNameError={newModuleNameError}
          setNewModuleNameError={setNewModuleNameError}
          newModuleSearch={newModuleSearch}
          setNewModuleSearch={setNewModuleSearch}
          isCreatingModule={isCreatingModule}
          setIsNewModuleMenuOpen={setIsNewModuleMenuOpen}
          newModuleNameInputRef={newModuleNameInputRef}
          sidebarModuleItems={sidebarModuleItems}
          isModuleTypeHidden={isModuleTypeHidden}
          handleCreateModule={handleCreateModule}
          isTeamWorkspace={selectedContainer?.type === 'team'}
        />
      )}

      {/* Mobile profile + settings at sidebar bottom */}
      {isMobile && (
        <div className="mobile-sidebar-profile-section">
          <div className="mobile-sidebar-divider" />
          <button
            className="mobile-sidebar-profile-row"
            onClick={() => {
              onCloseSidebar?.();
              // Open the unified WorkspaceOptions sheet (same as desktop's
              // SidebarRail avatar) instead of the duplicate mobile sheet.
              setTimeout(() => window.dispatchEvent(new CustomEvent('openWorkspaceOptions')), 300);
            }}
          >
            <span className="mobile-sidebar-profile-avatar">
              {(profile?.name || profile?.email || 'A').charAt(0).toUpperCase()}
            </span>
            <span className="mobile-sidebar-profile-name">
              {profile?.name || profile?.email?.split('@')[0] || 'Account'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 15 12 9 18 15" />
            </svg>
          </button>
        </div>
      )}


    </aside>

  </>
  );
}
